import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE } from './chartBase';
import { COLOR, FUENTE } from './tokens';
import { formatearTipoTramite } from '../../../constants/tiposTramiteTurno';
import PeriodoUnicoKpi from './PeriodoUnicoKpi';
import InfoGrafico from './InfoGrafico';
import { MOCK_TENDENCIA_POR_TIPO } from './mockDataEjemplos';
import { seriesTendenciaPorTipo } from './calculosReportes';

/**
 * Tendencia mensual del resultado de la atención, desglosada mes a mes. Pensada para
 * usarse con el filtro de tipo de trámite del panel superior fijado en un valor puntual
 * (por eso recibe tipoTramiteActivo: para que el título sea honesto sobre qué se está
 * mostrando). Si no hay ningún tipo elegido, agrega todos los trámites juntos.
 *
 * Una serie por resultado del catálogo (V224): antes eran 3 columnas fijas y todo lo que el
 * estudio agregara o renombrara en Datos Maestros caía en "otros / sin clasificar".
 * @param {{
 *   data: Array<{anio: number, mes: number, total: number, resultados: Array<{resultado: string, resultadoLabel: string, cantidad: number}>}>,
 *   tipoTramiteActivo: string|null,
 * }} props
 */
const TendenciaPorTipoChart = forwardRef(({ data, tipoTramiteActivo, compacto = false }, ref) => {
    const { meses, series } = useMemo(() => seriesTendenciaPorTipo(data), [data]);

    const chartData = {
        labels: meses.map((m) => m.label),
        datasets: series.map((s) => ({
            label: s.etiqueta, data: s.valores, backgroundColor: s.color, stack: 't', borderRadius: 3,
        })),
    };

    const options = {
        ...BASE_OPTIONS,
        scales: {
            x: { ...EJE_X_BASE, stacked: true },
            y: { ...EJE_Y_BASE, stacked: true },
        },
    };

    const subtitulo = tipoTramiteActivo
        ? `Trámite "${formatearTipoTramite(tipoTramiteActivo)}", mes a mes.`
        : 'Todos los trámites juntos — elegí un tipo en el filtro de arriba para ver el desglose de uno solo.';

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Tendencia por tipo de trámite"
                        queMuestra="Te dice cómo evoluciona mes a mes la composición del resultado de la atención (cuánto se resuelve en el momento vs. queda pendiente vs. no prospera) para un tipo de trámite puntual, o para todos juntos."
                        ejemploVisual={<TendenciaPorTipoChart compacto data={MOCK_TENDENCIA_POR_TIPO} tipoTramiteActivo={null} />}
                        comoLeerlo={[
                            'Con menos de 2 meses de datos, cada resultado se muestra como una cifra simple — un solo mes se vería como un bloque sólido sin nada que comparar.',
                            'Con 2 o más meses, cada barra es un mes, apilando un color por resultado de la atención — la altura total de la barra es el total de atenciones de ese mes.',
                            'Aparece un color por cada resultado cargado en Datos Maestros que tuvo turnos en el período. Los de siempre mantienen su color: verde = inicia ahora, azul = carpeta a futuro, ámbar = no quiso dejar carpeta, rojo = no viable, gris = sin clasificar; los que agregue el estudio van en tonos claros.',
                            'El subtítulo aclara si se está mirando un tipo de trámite puntual (según el filtro de arriba) o todos juntos.',
                        ]}
                        analisis="La porción verde (inicia ahora) crece de 33% del total en junio a 41% en agosto, mientras la roja (no viable) baja de 23% a 17% en el mismo período — la composición mejora mes a mes, no solo el volumen total. Sin clasificar se mantiene chico y estable (13-15%), así que no está tapando la lectura del resto."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Tendencia por tipo de trámite</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        Cómo evoluciona el resultado de la atención, un color por resultado. {subtitulo}
                    </Typography>
                </>
            )}
            {meses.length === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto }}>
                    No hay datos suficientes para graficar la tendencia.
                </Typography>
            ) : meses.length < 2 ? (
                <PeriodoUnicoKpi items={series.map((s) => ({
                    etiqueta: s.etiqueta, valor: s.valores[0].toLocaleString('es-AR'), color: s.color,
                }))} />
            ) : (
                <Box sx={{ height: compacto ? 160 : 260 }}>
                    <Bar ref={ref} data={chartData} options={options} />
                </Box>
            )}
        </Card>
    );
});

export default TendenciaPorTipoChart;
